import type { PkMeshVertex } from './pkFromMesh';
import {
  comparePk,
  pkIsBetween,
  pkMinMax,
  sortByPk,
  type PkLike,
} from './pkCompare';

export interface MeshPathPoint {
  lat: number;
  lng: number;
}

interface SegmentPks {
  pkInitKm?: number | null;
  pkInitMeter?: number | null;
  pkEndKm?: number | null;
  pkEndMeter?: number | null;
}

/** PK a metros absolutos: comparar por campo se rompe al cruzar de kilómetro. */

/**
 * Traza un tramo CERRADO a partir de la malla `GeoPositionRoad` precacheada,
 * sin tocar la red.
 *
 * Por qué existe: `MapWithSegments.loadSegmentCoordinates` resuelve la
 * polilínea de cada tramo con `GET_POSITION_ROADS` (`cache-first`). Offline,
 * si esas variables exactas no estaban ya en la caché de Apollo, la query
 * revienta y el tramo se quedaba **sin dibujar**. Con el mapa base offline eso
 * pasó de invisible a evidente: fondo y traza viva sí, tramos cerrados no.
 *
 * La malla que la app descarga al arrancar (`ensureRoadMesh`, guardada en
 * `roadMeshStore`) ya
 * trae `latitude`/`longitude` por vértice, así que basta recortarla por el
 * rango de PK del tramo. Es el mismo material con el que `pkFromMesh` resuelve
 * el PK sin red — aquí se reutiliza para el trazado.
 *
 * LÍMITES ACEPTADOS (mejor esfuerzo, igual que `pkFromMesh`):
 * - Resolución la de la malla (~100 m), sin la interpolación del backend.
 * - La malla se cachea por `roadId`, no por sentido: un tramo del sentido
 *   contrario se dibuja sobre la geometría de la otra calzada. A escala de
 *   mapa son metros, y sin red es eso o no dibujar nada.
 *
 * Devuelve el orden REAL de patrulla (invertido en los descendentes), igual
 * que hace `loadSegmentCoordinates` con la respuesta del backend, que siempre
 * llega en PK ascendente.
 */
export function segmentPathFromMesh(
  segment: SegmentPks,
  mesh: PkMeshVertex[]
): MeshPathPoint[] {
  const { pkInitKm, pkInitMeter, pkEndKm, pkEndMeter } = segment;
  if (
    pkInitKm == null ||
    pkInitMeter == null ||
    pkEndKm == null ||
    pkEndMeter == null ||
    mesh.length === 0
  ) {
    return [];
  }

  const start: PkLike = { km: pkInitKm, meter: pkInitMeter };
  const end: PkLike = { km: pkEndKm, meter: pkEndMeter };
  const [low, high] = pkMinMax(start, end);

  const inRange = sortByPk(
    mesh
      .map((vertex) => ({
        km: vertex.pkKm,
        meter: vertex.pkMeter,
        lat: vertex.latitude,
        lng: vertex.longitude,
      }))
      .filter((vertex) => pkIsBetween(vertex, low, high))
  );

  // Con menos de 2 vértices no hay línea que dibujar. Devolver 1 punto haría
  // que el caller lo tomara por un tramo válido y pintara una polilínea
  // degenerada; mejor vacío y que se comporte como "sin datos".
  if (inRange.length < 2) return [];

  const path = inRange.map(({ lat, lng }) => ({ lat, lng }));
  return comparePk(start, end) > 0 ? path.reverse() : path;
}

export default segmentPathFromMesh;
